import React, { useState, useEffect, useMemo, useCallback } from 'react';
import RaceMap from './components/RaceMap';
import Sidebar from './components/Sidebar';
import { ToastContainer } from './components/Toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { fetchSpreadsheetData, extractStravaActivityId } from './utils/spreadsheet';
import { fetchMultipleRoutes } from './utils/strava';
import './App.css';

function App() {
  const [races, setRaces] = useState([]);
  const [routes, setRoutes] = useState({});
  const [selectedRace, setSelectedRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [filters, setFilters] = useState({
    showMarathons: true,
    showUltras: true,
    showRoad: true,
    showTrail: true,
    year: 'all'
  });

  // Toast management
  const addToast = useCallback((message, type = 'error', retryable = false, duration = 5000) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, message, type, retryable, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Try to load races from cached JSON first
      let racesData = null;
      try {
        const racesResponse = await fetch('/races-data.json');
        if (racesResponse.ok) {
          const racesCache = await racesResponse.json();
          racesData = racesCache.races;
        }
      } catch {
        // Cache not found, will fallback to spreadsheet
      }

      // Fallback to spreadsheet if cache not available
      if (!racesData) {
        racesData = await fetchSpreadsheetData();
      }

      setRaces(racesData);

      // Extract activity IDs
      const activityIds = racesData
        .map(race => extractStravaActivityId(race.strava))
        .filter(id => id !== null);

      // Load routes from cached JSON
      try {
        const routesResponse = await fetch('/routes-data.json');
        if (routesResponse.ok) {
          const routesDatabase = await routesResponse.json();

          const loadedRoutes = {};
          activityIds.forEach(id => {
            if (routesDatabase.routes[id]) {
              loadedRoutes[id] = routesDatabase.routes[id].coordinates;
            }
          });

          setRoutes(loadedRoutes);
          setLoading(false);
          return;
        }
      } catch {
        // Cache not found, will fallback to Strava API
      }

      // Fallback: Fetch from Strava (should rarely happen)
      const routesData = await fetchMultipleRoutes(activityIds, (prog) => {
        setProgress(prog);
      });

      setRoutes(routesData);

    } catch (error) {
      const errorMessage = error.message === 'STRAVA_AUTH_FAILED'
        ? 'Strava authentication failed. Please check your API credentials.'
        : error.message === 'TOKEN_REFRESH_FAILED'
        ? 'Failed to refresh Strava token. Please re-authenticate.'
        : 'Failed to load race data. Please try again.';
      addToast(errorMessage, 'error', true);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  // Helper to extract year from date string (supports DD/MM/YYYY and YYYY-MM-DD)
  const extractYear = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    // Try DD/MM/YYYY format
    const slashParts = dateStr.split('/');
    if (slashParts.length === 3 && slashParts[2].length === 4) {
      return slashParts[2];
    }
    // Try YYYY-MM-DD format
    const dashParts = dateStr.split('-');
    if (dashParts.length === 3 && dashParts[0].length === 4) {
      return dashParts[0];
    }
    return null;
  };

  // Apply filters to races
  const getFilteredRaces = () => {
    return races.filter(race => {
      // Type filters
      if (race.type === 'Marathon' && !filters.showMarathons) return false;
      if (race.type === 'Ultra' && !filters.showUltras) return false;

      // Terrain filters
      if (race.terrain === 'Road' && !filters.showRoad) return false;
      if (race.terrain === 'Trail' && !filters.showTrail) return false;

      // Year filter
      if (filters.year !== 'all') {
        const raceYear = extractYear(race.date);
        if (raceYear !== filters.year) return false;
      }

      return true;
    });
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleRaceSelect = (race) => {
    setSelectedRace(race);
  };

  const filteredRaces = getFilteredRaces();

  // Filter routes to match filtered races - memoized for performance
  const filteredRoutes = useMemo(() => {
    const result = {};
    filteredRaces.forEach(race => {
      const activityId = extractStravaActivityId(race.strava);
      if (activityId && routes[activityId]) {
        result[activityId] = routes[activityId];
      }
    });
    return result;
  }, [filteredRaces, routes]);

  return (
    <ThemeProvider>
      <div className="App">
        <Sidebar
          races={filteredRaces}
          selectedRace={selectedRace}
          onRaceSelect={handleRaceSelect}
          loading={loading}
          progress={progress}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
        <div className="map-container">
          <RaceMap
            races={filteredRaces}
            routes={filteredRoutes}
            selectedRace={selectedRace}
            onRaceSelect={handleRaceSelect}
          />
        </div>
        <ToastContainer
          toasts={toasts}
          removeToast={removeToast}
          onRetry={loadData}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
